import { CustomerData, CustomerDataHistory, WorkflowExecution, WorkflowStatus } from "../services";
import { ScheduledEventInfo } from "../services/ScheduledEventsService";
import { RedrivePollResult } from "../services/RedriveService";
import { Realm, TABLE_NAMES, TABLE_LABELS, LABELS } from "../constants";
import { DateFormatter } from "./DateFormatter";
import { AwsUrlBuilder } from "./AwsUrlBuilder";

export interface SearchResultsSummary {
  customerId: string;
  realm: Realm;
  data: CustomerData | null;
  history: CustomerDataHistory | null;
  workflows: WorkflowExecution[];
  scheduledEvents: ScheduledEventInfo[];
}

const NONE_FOUND = "None found";

const sanitizeCell = (value: string): string => value.replace(/\r?\n/g, " ").replace(/\|/g, "\\|");

const buildTable = (headers: string[], rows: string[][]): string[] => [
  `| ${headers.join(" | ")} |`,
  `| ${headers.map(() => "---").join(" | ")} |`,
  ...rows.map((row) => `| ${row.map(sanitizeCell).join(" | ")} |`),
];

const formatUtcDate = (date?: string, fallback: string = LABELS.EMPTY_VALUE): string => DateFormatter.formatDate(date, fallback, "utc");

export class ResultFormatter {
  static formatSearchResults(summary: SearchResultsSummary): string {
    const lines: string[] = ["## Customer Search Results", `Customer ID: ${summary.customerId}`, `Realm: ${summary.realm}`, ""];

    const foundTables = summary.data ? TABLE_NAMES.filter((table) => summary.data?.[table] != null) : [];
    lines.push(`### Tables (${foundTables.length}/${TABLE_NAMES.length} found)`);
    TABLE_NAMES.forEach((table) => {
      const found = summary.data?.[table] != null;
      const historyCount = summary.history?.[table]?.length || 0;
      const historySuffix = found && historyCount > 0 ? ` (${historyCount} history versions)` : "";
      lines.push(`- ${TABLE_LABELS[table]}: ${found ? "found" : "not found"}${historySuffix}`);
    });
    lines.push("");

    const failedCount = summary.workflows.filter((workflow) => workflow.status === WorkflowStatus.FAILED).length;
    lines.push(`### Workflows (${summary.workflows.length} total, ${failedCount} failed)`);
    if (summary.workflows.length === 0) {
      lines.push(NONE_FOUND);
    } else {
      lines.push(
        ...buildTable(
          ["Workflow", LABELS.STATUS, LABELS.STARTED, LABELS.COMPLETED, LABELS.DURATION],
          summary.workflows.map((workflow) => [
            workflow.workflowName,
            workflow.status,
            formatUtcDate(workflow.startDate),
            formatUtcDate(workflow.stopDate, LABELS.RUNNING),
            DateFormatter.formatDuration(workflow.startDate, workflow.stopDate, LABELS.RUNNING),
          ])
        )
      );
    }
    lines.push("");

    lines.push(`### ${LABELS.SCHEDULED_EVENTS} (${summary.scheduledEvents.length})`);
    if (summary.scheduledEvents.length === 0) {
      lines.push(NONE_FOUND);
    } else {
      lines.push(
        ...buildTable(
          [LABELS.SCHEDULE_GROUP, "Expression", LABELS.STATUS, LABELS.TARGET],
          summary.scheduledEvents.map((schedule) => [
            schedule.group,
            schedule.scheduleExpression,
            schedule.state,
            schedule.targetArn ? AwsUrlBuilder.extractResourceName(schedule.targetArn) : LABELS.EMPTY_VALUE,
          ])
        )
      );
    }

    return lines.join("\n");
  }

  static formatRedriveResults(pollResult: RedrivePollResult | null): string {
    if (!pollResult) {
      return "";
    }

    const lines: string[] = ["## Redrive Results"];

    if (pollResult.workflowError) {
      lines.push(`Workflow error: ${pollResult.workflowError.error}: ${pollResult.workflowError.cause}`);
    }

    const result = pollResult.redriveByDatesResult;
    if (result) {
      lines.push(`Attempted: ${result.attemptedRedrives} | Successful: ${result.successfullyLaunched} | Failed: ${result.failedToLaunch}`);

      if (result.newExecutionDescriptions.length > 0) {
        lines.push("", `### New Executions (${result.newExecutionDescriptions.length})`);
        lines.push(
          ...buildTable(
            ["Execution", LABELS.STATUS, LABELS.STARTED],
            result.newExecutionDescriptions.map((execution) => [
              AwsUrlBuilder.extractResourceName(execution.executionArn),
              execution.status,
              formatUtcDate(execution.startDate),
            ])
          )
        );
      }

      if (result.redriveErrors.length > 0) {
        lines.push("", `### Errors (${result.redriveErrors.length})`);
        lines.push(
          ...buildTable(
            ["Failed Execution", LABELS.ERROR],
            result.redriveErrors.map((redriveError) => [
              AwsUrlBuilder.extractResourceName(redriveError.parentFailedExecution),
              redriveError.error?.errorMessage ?? LABELS.EMPTY_VALUE,
            ])
          )
        );
      }
    }

    return lines.join("\n");
  }
}
